import userStore from "@/store/user";
import layoutStore from "@/store/layout";
import { rotasAbertas, rotasRestritas } from "./agregadorRotas";
import { AbilityTuple, MongoAbility, MongoQuery, subject } from "@casl/ability";
import {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
} from "vue-router";
import * as Sentry from "@sentry/vue";
import { pode } from "@/shared/autorizacao";

export const ROTAS_IGNORADAS = ["/404", "/403", "/acessoFederado", "/login"];

/**
 * Formato minimo que os guards de acesso precisam da rota — nao e
 * `RouteLocationNormalized` porque os testes chamam essas funcoes com
 * fixtures mais simples (ver src/router/__tests__/helpers.spec.ts).
 */
type RotaGuard = {
  path: string;
  query: Record<string, unknown>;
  params: Record<string, unknown>;
  meta: Record<string, unknown>;
};

type Redirecionar = (destino?: string) => void;

// TODO: AINDA NÃO FIQUEI FELIZ COM ESSA FUNÇÃO, ELA FAZ DUAS COISAS VERIFICAR ACESSO E REDIRECIONAR, DEVERIA APENAS VERIFICAR
export function verificarAcesso(pathsAbertos: string[], to: RotaGuard, next: Redirecionar) {
  if (pathsAbertos.includes(to.path)) return true;

  try {
    const user = userStore().user;

    if (!user.email) {
      const query = to.query.redirect ? `?redirect=${String(to.query.redirect)}` : "";
      next(`/login${query}`);
      return false;
    }

    Sentry.setUser({
      roles: user.roles,
      email: user.email,
      tipo: user.tipo,
      empresa: user.empresa, // Útil se você usa nicknames como 'CaioPK'
      ip_address: "{{auto}}", // O Sentry captura o IP automaticamente para geolocalização
    });

    if (to.params.obra) return possuiAcesso(to.meta.subject, to.meta.action, to.params.obra);

    if (possuiAcesso(to.meta.subject, to.meta.action)) return true;

    next("/403");
  } catch {
    next("/403");
    return false;
  }
}
/**
 * Código de obra recebido pelo parâmetro de rota — string, array (o tipo de
 * `RouteParams` admite, ainda que nenhuma rota hoje repita `:obra`) ou
 * ausente. Normalizado para string única, ou `undefined`.
 */
function codigoDaObra(bruto: unknown): string | undefined {
  if (typeof bruto === "string" && bruto) return bruto;
  if (Array.isArray(bruto) && typeof bruto[0] === "string" && bruto[0]) return bruto[0];
  return undefined;
}

/**
 * Cliente só acessa a obra do próprio vínculo; Gali lê qualquer uma —
 * concessão incondicional de `papeis/obra.ts` (`PADRONIZACAO-ROLES.md §8.1`).
 * O registro aqui é o PROTÓTIPO mínimo — só o campo que o escopo confere
 * (`Id_Lista`, `CAMPO_ESCOPO.Obra`), não a obra inteira — o mesmo padrão do
 * protótipo de `create` de RDO (T62): evita a Api/rede dentro do guard, e
 * `pode` (T61/T63, `aplicarEscopo`) decide o resto.
 */
function podeLerObra(codigo: string): boolean {
  return pode("read", subject("Obra", { Id_Lista: codigo }));
}

// TODO: AINDA FALTA REFATORAR ESSE
export function possuiAcesso(
  recursosNecessarios: unknown,
  acoes: unknown = [],
  obra: unknown = [],
) {
  const userAbility = userStore().ability as MongoAbility<AbilityTuple, MongoQuery>;
  const recursos = recursosNecessarios as string[] | undefined;
  const listaAcoes = (acoes as string[] | undefined) ?? [];

  const liberadoPeloRecurso =
    !recursos || !recursos.length
      ? true
      : recursos.some((recurso) => {
          if (listaAcoes.length) return listaAcoes.some((acao) => userAbility.can(acao, recurso));
          return userAbility.can("read", recurso);
        });

  if (!liberadoPeloRecurso) return false;

  // `_obra` era ignorado (T64, ESC-04): o parâmetro chegava e nada o
  // consultava, então navegar direto por URL para a obra de outro cliente
  // passava — o `can(acao, "Obra")` em string (AD-046) nunca via a condição
  // de escopo que a T61/T63 escreveram. Agora o parâmetro decide de verdade.
  const codigo = codigoDaObra(obra);
  if (!codigo) return liberadoPeloRecurso;

  return podeLerObra(codigo);
}

let layout: ReturnType<typeof layoutStore> | null = null;

type RotaComMatched = {
  matched: unknown[];
};

// TODO: AINDA FALTA REFATORAR ESSE
export async function criarBreadcrumbs(rota: RotaComMatched) {
  try {
    if (layout == null) {
      layout = layoutStore();
    }

    let crumbs: string[] = ["Home"];
    rota.matched.forEach((registro) => {
      const el = registro as { name?: unknown; meta?: { breadcrumb?: string } };
      if (el.name === "Home") {
        return;
      }
      if (el.meta?.breadcrumb) {
        const todosPaths = el.meta.breadcrumb.split("|");
        crumbs = crumbs.concat(todosPaths);
      }
      if (typeof el.name === "string") crumbs.push(el.name);
    });
    const crumbTratado = crumbs.filter((e) => e);
    await layout.setBreadcrumbs(crumbTratado);
  } catch (e) {
    // eslint-disable-next-line no-console -- erro de breadcrumb nao pode quebrar a navegacao; visibilidade minima em console
    console.error("Erro ao criar breadcrumbs", e);
  }
}

/**
 * Devolve `true` quando redireciona (T65, ESC-05) — antes o retorno era
 * `void`, e `router/index.ts` chamava esta função e seguia em frente sem
 * checar nada, avaliando `verificarAcesso` (que também chama `next`) na
 * MESMA navegação. Duas chamadas de `next()` na mesma passagem do guard:
 * a segunda é ignorada pelo Vue Router, mas em corrida com um `next` que
 * nega (`/403`) o comportamento passa a depender de qual delas o router
 * processa primeiro. O chamador agora `return`s quando este `true` volta.
 */
export function converterHashFederado(
  to: RouteLocationNormalized,
  next: NavigationGuardNext,
): boolean {
  if (to.path == "/acessoFederado" && !!to.hash) {
    const novoPath = `/acessoFederado?${to.hash.substring(1)}`;
    next(novoPath);
    return true;
  }
  return false;
}

export function filtrarRotasSeNecessario(
  from: RouteLocationNormalized,
  START_LOCATION: RouteLocationNormalizedLoaded,
) {
  if (
    from === START_LOCATION ||
    (from && ["Login", "AcessoFederado", "PrimeiroAcesso"].includes(from.name as string))
  ) {
    layoutStore().filtrarRotasComAcesso(rotasAbertas, rotasRestritas);
  }
}

export function redirecionarUltimaRota(
  from: RouteLocationNormalized,
  to: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  if (from.name && ["AcessoFederado"].includes(from.name as string)) {
    const store = userStore();
    if (store.ultimaRotaVisitada && !to.redirectedFrom) {
      if (to.path !== store.ultimaRotaVisitada) {
        if (store.ultimaRotaVisitada.includes("?")) {
          next({
            path: store.ultimaRotaVisitada.split("?")[0],
            query: Object.fromEntries(new URLSearchParams(store.ultimaRotaVisitada.split("?")[1])),
          });
        } else next({ path: store.ultimaRotaVisitada });
        return true;
      }
    }
  }
  return false;
}
